import React from 'react';
import { Scale } from 'lucide-react';
import HowToRead from './HowToRead';

export interface CompositeBridgePillar {
  id: string;
  label: string;
  score: number;
  weight: number;
  contribution_pp: number;
  status: 'MEASURED' | 'ESTIMATED' | 'PARTIAL' | 'WITHHELD' | string;
  nominal_weight?: number; // SR-145: class-framework nominal weight
  evidence_class?: string; // SR-174: OBSERVED / CURATED_VERIFIED / MODEL_ESTIMATED / PARTIAL / WITHHELD
}

export interface CompositeBridgePenalty {
  id: string;
  label: string;
  value: number;
}

export interface CompositeBridgePillarChange {
  id: string;
  label: string;
  prev_pp: number;
  curr_pp: number;
  delta_pp: number;
}

export interface CompositeBridgeShape {
  baseline: number;
  pillars: CompositeBridgePillar[];
  penalties?: CompositeBridgePenalty[];
  total: number;
  formula: string;
  changes_since_last?: CompositeBridgePillarChange[] | null;
  // P0-30: the SINGLE cross-surface authority for the "strongest measured driver" claim, sealed by
  // the backend so the Analysis page and the Dossier can never name different drivers. measured_exclusions
  // are the higher-contribution pillars held back as not measurement-grade (printed so the rule is visible).
  strongest_measured_id?: string;
  measured_exclusions?: CompositeBridgePillar[];
  // SR-145/153/155: framework, coverage, renormalization and the full-precision canonical score.
  framework_ref?: string;
  nominal_coverage?: number;
  renormalization_factor?: number;
  canonical_score?: number;
  contribution_unit?: string;
  versions?: { framework_version?: string; scoring_model_version?: string; bridge_policy_version?: string };
  display_reconciliation?: CompositeBridgePenalty | null;
  non_scoring_diagnostics?: CompositeBridgePenalty[];
  // SR-276: the class framework withheld the composite — pillar rows are diagnostics, no total.
  composite_withheld?: boolean;
  composite_withheld_reason?: string;
  composite_withheld_flags?: string[];
  scoreable_nominal_coverage?: number;
}

interface Props {
  bridge?: CompositeBridgeShape | null;
  className?: string;
}

const signed = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)} pts`;

const statusChip = (status: string, evidenceClass?: string) => {
  const base = 'rounded px-1.5 py-[1px] text-[10px] font-mono border';
  // SR-174: a primary-source-verified regulatory ruleset is CURATED_VERIFIED, not an observed measurement.
  if (evidenceClass === 'CURATED_VERIFIED') {
    return <span className={`${base} bg-slate-800/40 border-slate-600 text-slate-200`} title="Curated rule-table, verified against primary legal sources — not an observed market measurement.">CURATED_VERIFIED</span>;
  }
  if (status === 'MEASURED') {
    return <span className={`${base} bg-emerald-900/30 border-emerald-800/60 text-emerald-300`}>OBSERVED</span>;
  }
  // ESTIMATED — the pillar counts in the composite but stands on a heuristic/model tier (e.g. a
  // curated audit-coverage estimate), NOT measurement-grade. Amber, distinct from MEASURED.
  if (status === 'ESTIMATED') {
    return <span className={`${base} bg-amber-900/25 border-amber-800/60 text-amber-300`} title="Pillar is scored but not measurement-grade — built on a heuristic/model tier, not directly observed feeds.">ESTIMATED</span>;
  }
  // PARTIAL — the pillar is measured on only part of its components (e.g. ESG is E-only; S/G not measured).
  if (status === 'PARTIAL') {
    return <span className={`${base} bg-amber-900/25 border-amber-800/60 text-amber-300`} title="Pillar is measured on only some of its components (the rest are not covered) — read as partial coverage.">PARTIAL</span>;
  }
  if (status === 'WITHHELD') {
    return <span className={`${base} bg-slate-800/50 border-slate-700 text-slate-300`} title="Pillar has no measurable feed for this asset — dropped from the composite (renormalized out), not scored as zero.">WITHHELD</span>;
  }
  return <span className="text-[10px] font-mono text-cmd-textDim">{status}</span>;
};

/**
 * Audit-quality composite bridge: baseline (50) + weighted pillar contributions − penalties = total.
 * Every narrative surface's "weakest / primary-watch / strongest" claim must derive from argmin/argmax
 * of contribution_pp in THIS table; the narrative-readback selector_mismatch gate enforces that.
 * When formula is prefixed "MISMATCH:" the bridge and the aggregator disagree — rendered in red so
 * the drift is visible instead of silent.
 */
const CompositeBridgePanel: React.FC<Props> = ({ bridge, className }) => {
  if (!bridge || !Array.isArray(bridge.pillars) || bridge.pillars.length === 0) return null;
  const mismatch = typeof bridge.formula === 'string' && bridge.formula.startsWith('MISMATCH');
  if (bridge.composite_withheld) {
    return (
      <div className={`rounded-md bg-[color:var(--c-panel-black)] border border-amber-400/25 p-4 ${className ?? ''}`}>
        <div className="flex items-center gap-2 mb-2">
          <Scale size={14} className="text-cmd-textDim" />
          <span className="text-sm font-semibold text-cmd-text">Quality composite · WITHHELD</span>
          <span className="text-[10px] font-mono text-cmd-textDim">{bridge.framework_ref || ''}{typeof bridge.scoreable_nominal_coverage === 'number' ? ` · scoreable nominal coverage ${(bridge.scoreable_nominal_coverage * 100).toFixed(0)}%` : ''}</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {(bridge.composite_withheld_flags ?? []).map((f) => (
            <span key={f} className="font-mono text-[10px] px-1.5 py-[1px] rounded border border-amber-400/40 text-amber-300/90">{f}</span>
          ))}
        </div>
        <p className="text-[11px] text-cmd-textDim mb-2">Coverage gate precedes renormalization: no composite, no contributions, no grade. Pillars below are diagnostics only.</p>
        <table className="w-full text-xs"><tbody>
          {bridge.pillars.map((p) => (
            <tr key={p.id} className="border-t border-[color:rgb(var(--c-ink-rgb)/0.04)]">
              <td className="py-1 text-cmd-textMuted">{p.label}</td>
              <td className="py-1 text-right font-mono tabular-nums text-cmd-text">{p.status === 'WITHHELD' ? '—' : `${p.score.toFixed(1)}/100`}</td>
              <td className="py-1 pl-3 text-right">{statusChip(p.status, (p as any).evidence_class)}</td>
              <td className="py-1 pl-3 text-right font-mono text-[10px] text-cmd-textDim">{typeof (p as any).nominal_weight === 'number' ? `nominal ${((p as any).nominal_weight * 100).toFixed(0)}%` : ''}</td>
            </tr>
          ))}
        </tbody></table>
      </div>
    );
  }

  return (
    <div className={`rounded-md bg-[color:var(--c-panel-black)] border border-[color:rgb(var(--c-ink-rgb)/0.06)] p-4 ${className ?? ''}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3">
        <Scale size={14} className="text-cmd-textDim" />
        <span className="text-sm font-semibold text-cmd-text">Composite bridge</span>
        <span className="text-[10px] font-mono text-cmd-textDim">(baseline + Σ pillar contribs − Σ penalties = total)</span>
      </div>
      <div className="mb-3">
        <HowToRead explainKey="composite_bridge" />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-[12px] font-mono tabular-nums [&_td:not(:first-child)]:pl-4 [&_th:not(:first-child)]:pl-4">
          <thead>
            <tr className="text-cmd-textDim">
              <th className="text-left font-normal py-1">Row</th>
              <th className="text-right font-normal py-1">Score</th>
              <th className="text-right font-normal py-1">Weight</th>
              <th className="text-right font-normal py-1">Contribution</th>
              <th className="text-left font-normal py-1 pl-3">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-[color:rgb(var(--c-ink-rgb)/0.05)]">
              <td className="py-1 text-cmd-text">Baseline</td>
              <td className="py-1 text-right text-cmd-text">{bridge.baseline.toFixed(2)}</td>
              <td className="py-1 text-right text-cmd-textDim">—</td>
              <td className="py-1 text-right text-cmd-textDim">—</td>
              <td className="py-1 pl-3 text-cmd-textDim">midpoint</td>
            </tr>
            {bridge.pillars.map((p) => {
              const isWithheld = p.status === 'WITHHELD';
              return (
                <tr key={p.id} className="border-t border-[color:rgb(var(--c-ink-rgb)/0.05)]">
                  <td className="py-1 text-cmd-text">{p.label}</td>
                  <td className={`py-1 text-right ${isWithheld ? 'text-cmd-textDim' : 'text-cmd-text'}`}>{isWithheld ? '—' : p.score.toFixed(2)}</td>
                  <td className={`py-1 text-right ${isWithheld ? 'text-cmd-textDim' : 'text-cmd-text'}`}>{isWithheld ? '—' : p.weight.toFixed(4)}</td>
                  <td className={`py-1 text-right ${isWithheld ? 'text-cmd-textDim' : p.contribution_pp < 0 ? 'text-red-300' : 'text-emerald-300'}`}>
                    {isWithheld ? '—' : signed(p.contribution_pp)}
                  </td>
                  <td className="py-1 pl-3">{statusChip(p.status, p.evidence_class)}</td>
                </tr>
              );
            })}
            {(bridge.penalties ?? []).map((pen) => {
              // Penalties SUBTRACT from the composite, so the effective impact is −value: a positive
              // value lowers the score (shown −, red), a negative value adds back (shown +, neutral).
              // Pre-revenue audit P1: this fixes the "−-0.05" double-sign on a negative adjustment.
              const v = pen.value ?? 0;
              const impact = v >= 0 ? `−${Math.abs(v).toFixed(2)}` : `+${Math.abs(v).toFixed(2)}`;
              return (
                <tr key={pen.id} className="border-t border-[color:rgb(var(--c-ink-rgb)/0.05)]">
                  <td className="py-1 text-cmd-text">{pen.label}</td>
                  <td className="py-1 text-right text-cmd-textDim">—</td>
                  <td className="py-1 text-right text-cmd-textDim">—</td>
                  <td className={`py-1 text-right ${v >= 0 ? 'text-red-300' : 'text-emerald-300'}`} title={(pen as any).reason || ''}>{impact}</td>
                  <td className="py-1 pl-3 text-cmd-textDim">{(pen as any).status === 'APPLIED' ? 'penalty' : ((pen as any).status || 'penalty').toLowerCase()}</td>
                </tr>
              );
            })}
            <tr className="border-t border-[color:rgb(var(--c-ink-rgb)/0.1)] bg-[color:rgb(var(--c-ink-rgb)/0.05)]">
              <td className="py-1 text-cmd-text font-semibold">Total</td>
              <td className="py-1 text-right text-cmd-text font-semibold" colSpan={3}>{bridge.total.toFixed(2)}</td>
              <td className="py-1 pl-3 text-cmd-textDim">composite</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className={`mt-2 text-[10px] font-mono ${mismatch ? 'text-red-300' : 'text-cmd-textDim'}`}>
        {bridge.formula}
      </div>

      {/* Cross-surface pillar-drift changelog: only significant deltas (|Δ| ≥ 5pp on the
          contribution scale) since the previous emit for this asset. Panel review 2026-08-22
          flagged the class where a pillar quietly moved 20+ raw points between reports without
          any restatement line — this row makes the drift visible instead of silent. */}
      {Array.isArray(bridge.changes_since_last) && bridge.changes_since_last.length > 0 && (
        <div className="mt-2 rounded bg-amber-900/15 border border-amber-800/50 px-2 py-1.5 text-[10px] font-mono text-amber-200">
          <div className="mb-1 text-amber-300">Pillar drift since last emit:</div>
          <ul className="space-y-0.5">
            {bridge.changes_since_last.map((c) => (
              <li key={c.id}>
                <span className="text-cmd-text">{c.label}</span>
                {`: ${c.prev_pp >= 0 ? '+' : ''}${c.prev_pp.toFixed(2)} pts → ${c.curr_pp >= 0 ? '+' : ''}${c.curr_pp.toFixed(2)} pts `}
                <span className={c.delta_pp < 0 ? 'text-red-300' : 'text-emerald-300'}>
                  ({c.delta_pp >= 0 ? '+' : ''}{c.delta_pp.toFixed(2)} pts)
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CompositeBridgePanel;
